import { useState, useContext, useEffect, useRef } from 'react';
import { Typography, InputAdornment, FormControl, TextField, Button, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import validator from 'validator';
import { StepBanner } from './StepBanner';
import { KYC, Analytics } from 'lib/api';
import { AuthContext } from 'context/auth-context';
import CircularComponent from 'components/shared/CircularComponent';
import StatusSnackbar from 'components/shared/StatusSnackbar';
import CloseIcon from '@mui/icons-material/Close';
import { KYX_ANALYTICS_EVENTS } from './StartKYX';

const useStyles = makeStyles({
    dialogBody: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: '600px',
        height: '100%'
    },
    content: {
        margin: '2em',
        width: '40em'
    },
    contentTitle: {
        fontWeight: '700!important',
        color: '#182D55'
    },
    phoneBox: {
        marginTop: '2em'
    },

    verifyButton: {
        color: 'white!important',
        borderRadius: '25px!important',
        padding: '.5em 3em!important',
        margin: '.5em!important'
    },
    formControl: {
        width: '100%!important',
        rowGap: '1em'
    },
    resend: {
        cursor: 'pointer',
        color: '#2979ff'
    }
});

export const KYXSecondStep = ({ handleClose, incrementPage, activePage, handleInputChange, formValues, setFormValues }) => {
    const classes = useStyles();
    const [phoneVerified, setPhoneVerified] = useState(false);
    const [errorPhone, setErrorPhone] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [label, setLabel] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resendCode, setResendCode] = useState(false);
    const [disableButton, setDisableButton] = useState(true);
    const [disableButtonCode, setDisableButtonCode] = useState(true);
    const { userInfo } = useContext(AuthContext);
    const resendTimeout = useRef();

    const verifyNumber = () => {
        let val = formValues;
        let res = validator.isMobilePhone(val.phoneNumber, 'en-US');
        if (res) {
            const payload = {
                phoneNumber: '1' + val.phoneNumber.replace(/[-]/g, '')
            };
            if (val.phone !== ' ' && payload !== undefined) {
                setLoading(true);
                KYC.verifyPhone(payload)
                    .then(response => {
                        if (response.success === true) {
                            setLoading(false);
                            setPhoneVerified(true);
                        }
                        resendTimeout.current = setTimeout(() => {
                            setResendCode(true);
                        }, 60000);
                    })
                    .catch(error => {
                        setOpenAlert(true);
                        setLoading(false);
                        setLabel('Invalid phone number');
                    });
            }
        } else {
            setErrorPhone(true);
        }
    };

    const verifyFormat = () => {
        if (!formValues.phoneNumber.trim()) {
            setDisableButton(true);
            return;
        }
        let res = validator.isMobilePhone(formValues.phoneNumber, 'en-US');
        if (!res) {
            setErrorPhone(true);
            setDisableButton(true);
            return;
        } else {
            setDisableButton(false);
        }
        if (errorPhone) {
            setErrorPhone(false);
            setDisableButton(false);
        }
    };

    const verifyCode = () => {
        let val = formValues;
        const payload = {
            code: val.code
        };
        if (val.code !== ' ' && payload !== undefined) {
            setLoading(true);
            KYC.verifyCode(payload)
                .then(response => {
                    if (response.success === true) {
                        const body = {
                            step: KYX_ANALYTICS_EVENTS.STEP_2,
                            eventData: formValues
                        };
                        Analytics.createKYXEvent(body).catch(error => console.error(error));
                        setLoading(false);
                        incrementPage();
                    }
                })
                .catch(error => {
                    setLoading(false);
                    setOpenAlert(true);
                    setLabel('Incorrect code');
                });
        }
    };

    const cancelVerif = () => {
        if (resendTimeout?.current) {
            clearTimeout(resendTimeout.current);
        }
        setPhoneVerified(false);
        setResendCode(false);
    };

    useEffect(() => {
        if (userInfo.phone_number) {
            setFormValues({
                ...formValues,
                phoneNumber: userInfo.phone_number
            });
            incrementPage();
        }
    }, [userInfo, setFormValues, formValues, incrementPage]);

    const incrementStep = () => {};

    return (
        <Box className={classes.dialogBody}>
            <StepBanner activeStep={activePage} incrementStep={() => incrementStep()} />
            <Box className={classes.content}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography
                            variant="h5"
                            className={classes.contentTitle}
                            sx={{
                                fontSize: {
                                    lg: '23px',
                                    md: '23px'
                                },
                                fontWeight: '700'
                            }}>
                            Verify phone number
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontSize: {
                                    lg: '14px',
                                    md: '14px'
                                },
                                fontWeight: '500'
                            }}>
                            We will send you a code to verify your number
                        </Typography>
                    </Box>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                        sx={{ alignSelf: 'flex-start' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    className={classes.phoneBox}
                    flexDirection="column"
                    sx={{
                        fontSize: {
                            lg: '13px',
                            md: '13px'
                        }
                    }}>
                    <FormControl className={classes.formControl}>
                        <TextField
                            error={errorPhone}
                            sx={{
                                width: {
                                    lg: '70%',
                                    md: '70%',
                                    sm: '85%',
                                    xs: '100%'
                                }
                            }}
                            onInput={e => {
                                e.target.value = e.target.value.length > 10 ? e.target.value.slice(0, 10) : e.target.value;
                            }}
                            onKeyUp={e => (e.target.value.length === 10 ? verifyFormat() : false)}
                            id="phoneNumber"
                            disabled={phoneVerified}
                            placeholder="Phone number"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography> +1 </Typography>
                                    </InputAdornment>
                                )
                            }}
                            variant="outlined"
                            size="small"
                            onChange={handleInputChange}
                            helperText={errorPhone ? 'Please, enter a valid phone number' : ''}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: {
                                    lg: phoneVerified ? '70%' : 'auto',
                                    mg: phoneVerified ? '70%' : 'auto',
                                    sm: phoneVerified ? '70%' : 'auto',
                                    xs: '100%'
                                }
                            }}>
                            {phoneVerified ? (
                                <Typography variant="subtitle1" className={classes.resend} onClick={() => cancelVerif()}>
                                    Back
                                </Typography>
                            ) : null}
                            {phoneVerified === false ? (
                                loading ? (
                                    <CircularComponent />
                                ) : (
                                    <Box
                                        marginTop="16px"
                                        display="flex"
                                        justifyContent="flex-emd"
                                        sx={{
                                            width: {
                                                lg: '70%',
                                                md: '70%',
                                                sm: '85%',
                                                xs: '100%'
                                            }
                                        }}>
                                        <Button
                                            variant="contained"
                                            disabled={disableButton}
                                            onClick={verifyNumber}
                                            sx={{
                                                width: '209px',
                                                marginLeft: 'auto'
                                            }}>
                                            Verify Number
                                        </Button>
                                    </Box>
                                )
                            ) : resendCode ? (
                                <Typography variant="subtitle1" className={classes.resend} onClick={() => verifyNumber()}>
                                    Send code again
                                </Typography>
                            ) : null}
                        </Box>
                    </FormControl>
                </Box>
                {phoneVerified === true ? (
                    <Box className={classes.phoneBox} flexDirection="column">
                        <FormControl className={classes.formControl}>
                            <TextField
                                className={classes.phoneInput}
                                id="code"
                                placeholder="Enter Code"
                                variant="outlined"
                                size="small"
                                onKeyUp={e =>
                                    e.target.value.length === 6 ? setDisableButtonCode(false) : setDisableButtonCode(true)
                                }
                                onChange={handleInputChange}
                                sx={{
                                    width: {
                                        lg: '70%',
                                        md: '70%',
                                        sm: '85%',
                                        xs: '100%'
                                    }
                                }}
                            />
                            {loading ? (
                                <CircularComponent />
                            ) : (
                                <Box
                                    marginTop="16px"
                                    display="flex"
                                    alignItems="self-end"
                                    sx={{
                                        width: {
                                            lg: '70%',
                                            md: '70%',
                                            sm: '85%',
                                            xs: '100%'
                                        }
                                    }}>
                                    <Button
                                        variant="contained"
                                        disabled={disableButtonCode}
                                        onClick={verifyCode}
                                        sx={{
                                            width: '209px',
                                            marginLeft: 'auto'
                                        }}>
                                        Verify Code
                                    </Button>
                                </Box>
                            )}
                        </FormControl>
                    </Box>
                ) : null}
            </Box>
            <StatusSnackbar label={label} severity={'error'} open={openAlert} setOpenAlert={setOpenAlert} />
        </Box>
    );
};
